import { inject, Injectable, LOCALE_ID } from '@angular/core';

import type {
  AddressAutocompleteProvider,
  AddressSearchOptions,
  AddressSuggestion,
} from '@arya/shared/address-autocomplete-contract';

import { PHOTON_URL } from './photon-url';

interface PhotonFeature {
  readonly properties?: PhotonProperties;
  readonly geometry?: PhotonGeometry;
}

interface PhotonProperties {
  readonly name?: string;
  readonly osm_key?: string;
  readonly osm_value?: string;
  readonly street?: string;
  readonly housenumber?: string;
  readonly postcode?: string;
  readonly city?: string;
  readonly district?: string;
  readonly locality?: string;
  readonly state?: string;
  readonly county?: string;
  readonly country?: string;
  readonly countrycode?: string;
}

interface PhotonGeometry {
  readonly coordinates?: readonly number[];
}

const DEFAULT_LIMIT = 5;
const COUNTRY_FILTER_OVERFETCH_FACTOR = 4;
const MAX_PHOTON_LIMIT = 50;
const PLACE_OSM_KEY = 'place';
const CITY_OSM_VALUES: ReadonlySet<string> = new Set(['city', 'town', 'village', 'hamlet', 'municipality']);
const SUPPORTED_LANGS: ReadonlySet<string> = new Set(['en', 'de', 'fr']);
const FALLBACK_LANG = 'en';

@Injectable({ providedIn: 'root' })
export class PhotonAddressAutocompleteProvider implements AddressAutocompleteProvider {
  private readonly photonUrl = inject(PHOTON_URL);
  private readonly localeId = inject(LOCALE_ID);
  public readonly available = true;

  public async search(query: string, options?: AddressSearchOptions): Promise<readonly AddressSuggestion[]> {
    const trimmed = query.trim();
    if (trimmed.length === 0) return [];
    const limit = options?.limit ?? DEFAULT_LIMIT;
    const countryHint = this.normalizeCountryHint(options?.countryHint);
    const url = this.buildUrl(trimmed, this.requestLimit(limit, countryHint));
    const response = await this.fetchSafely(url, options?.signal);
    if (response === undefined) return [];
    const features = await this.parseSafely(response);
    const suggestions = features
      .map((feature) => this.toSuggestion(feature))
      .filter((s): s is AddressSuggestion => s !== undefined);
    return this.filterByCountry(suggestions, countryHint).slice(0, limit);
  }

  private normalizeCountryHint(countryHint: string | undefined): string {
    return countryHint?.trim().toUpperCase() ?? '';
  }

  private requestLimit(limit: number, countryHint: string): number {
    return countryHint.length === 0 ? limit : Math.min(limit * COUNTRY_FILTER_OVERFETCH_FACTOR, MAX_PHOTON_LIMIT);
  }

  private buildUrl(query: string, limit: number): string {
    const params = new URLSearchParams({
      q: query,
      limit: String(limit),
      lang: this.resolveLang(),
    });
    const endpoint = this.photonUrl.replace(/\/$/, '');
    return `${endpoint}/api?${params.toString()}`;
  }

  private resolveLang(): string {
    const lang = this.localeId.split('-')[0]?.toLowerCase() ?? '';
    return SUPPORTED_LANGS.has(lang) ? lang : FALLBACK_LANG;
  }

  private async fetchSafely(url: string, signal: AbortSignal | undefined): Promise<Response | undefined> {
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });
      return response.ok ? response : undefined;
    } catch {
      return undefined;
    }
  }

  private async parseSafely(response: Response): Promise<readonly PhotonFeature[]> {
    try {
      const data: unknown = await response.json();
      return this.extractFeatures(data).filter(
        (item): item is PhotonFeature => typeof item === 'object' && item !== null
      );
    } catch {
      return [];
    }
  }

  private extractFeatures(data: unknown): readonly unknown[] {
    if (typeof data !== 'object' || data === null || !('features' in data)) return [];
    const { features } = data;
    return Array.isArray(features) ? features : [];
  }

  private toSuggestion(feature: PhotonFeature): AddressSuggestion | undefined {
    const properties = feature.properties ?? {};
    const street = this.composeStreet(properties);
    const city = this.composeCity(properties);
    if (street.length === 0 && city.length === 0) return undefined;
    return {
      displayText: [street, city].filter((p) => p.length > 0).join(', '),
      street,
      city,
      region: properties.state ?? properties.county ?? '',
      postalCode: properties.postcode ?? '',
      countryCode: properties.countrycode?.toUpperCase() ?? '',
      countryName: properties.country ?? '',
      latitude: this.toCoordinate(feature.geometry?.coordinates?.[1]),
      longitude: this.toCoordinate(feature.geometry?.coordinates?.[0]),
    };
  }

  private composeStreet(properties: PhotonProperties): string {
    const street = [properties.street ?? '', properties.housenumber ?? ''].filter((p) => p.length > 0).join(' ');
    return street.length > 0 ? street : this.nameAsStreet(properties);
  }

  private nameAsStreet(properties: PhotonProperties): string {
    if (properties.osm_key === PLACE_OSM_KEY) return '';
    const name = properties.name ?? '';
    const placeLabels = [
      properties.city,
      properties.district,
      properties.locality,
      properties.state,
      properties.county,
      properties.country,
    ];
    return placeLabels.includes(name) ? '' : name;
  }

  private composeCity(properties: PhotonProperties): string {
    return properties.city ?? properties.district ?? properties.locality ?? this.nameAsCity(properties);
  }

  private nameAsCity(properties: PhotonProperties): string {
    const isCityPlace = properties.osm_key === PLACE_OSM_KEY && CITY_OSM_VALUES.has(properties.osm_value ?? '');
    return isCityPlace ? (properties.name ?? '') : '';
  }

  private filterByCountry(
    suggestions: readonly AddressSuggestion[],
    countryHint: string
  ): readonly AddressSuggestion[] {
    if (countryHint.length === 0) return suggestions;
    return suggestions.filter((suggestion) => suggestion.countryCode === countryHint);
  }

  private toCoordinate(value: number | undefined): number | undefined {
    return Number.isFinite(value) ? value : undefined;
  }
}
